import { useContext } from 'react';
import isEqual from 'lodash/isEqual';
import {
    isExcludeProductSelection,
    isIncludeProductSelection,
    ProductSelection,
    ProductSelectionAction
} from './ProductSelectionTypes';
import { LineItemKey } from '../../store/api';
import { ProductSelectionState } from '../../store/productSelection/state';
import { ProductMetaData } from '../../../models/Product';
import { Article } from '../../../models/Article';
import { mapToLineItemKey } from '../../store/productSelection/actions';
import { ProductSelectionContext } from './ProductSelectionContext';
import { useDispatch } from 'react-redux';
import { showNotification, TYPE_WARNING } from '../../store/notifications/actions';
import { useTranslation } from 'react-i18next';

export const MAX_SELECT = 10000;

export const useProductSelection = () => {
    const { t } = useTranslation();
    const dispatchRedux = useDispatch();
    const [state, dispatch] = useContext(ProductSelectionContext);

    const toggleSelectionMode = () => {
        state
            ? dispatch({ type: ProductSelectionAction.CLOSE })
            : dispatch({ type: ProductSelectionAction.INIT_INCLUDE_SELECTION });
    };

    const selectAll = (totalCount: number) => {
        if (totalCount > MAX_SELECT) {
            dispatchRedux(
                showNotification(t('notifications.useProductSelection.error', { maxSelect: MAX_SELECT }), TYPE_WARNING)
            );
        } else {
            dispatch({ type: ProductSelectionAction.INIT_EXCLUDE_SELECTION, totalCount });
        }
    };

    const deselectAll = () => dispatch({ type: ProductSelectionAction.INIT_INCLUDE_SELECTION });

    const onSelectButtonClick = (selectedProduct: LineItemKey | string) => {
        selectedProduct = mapProductIdToLineItemKey(selectedProduct);

        if (isIncludeProductSelection(state)) {
            const productIsIncluded = state.included.find(item => isEqual(item, selectedProduct));

            if (productIsIncluded) {
                dispatch({ type: ProductSelectionAction.DESELECT, keys: [selectedProduct] });
            } else {
                dispatch({ type: ProductSelectionAction.SELECT, keys: [selectedProduct] });
            }
        } else if (isExcludeProductSelection(state)) {
            const productIsExcluded = state.excluded.find(item => isEqual(item, selectedProduct));

            if (productIsExcluded) {
                dispatch({ type: ProductSelectionAction.SELECT, keys: [selectedProduct] });
            } else {
                dispatch({ type: ProductSelectionAction.DESELECT, keys: [selectedProduct] });
            }
        } else {
            dispatch({ type: ProductSelectionAction.SELECT, keys: [selectedProduct] });
        }
    };

    /**
     * @deprecated
     */
    const onSelectButtonClickDeprecated = (productOrId: string | (ProductMetaData & Article)) => {
        return onSelectButtonClick(mapToLineItemKey(productOrId));
    };

    return {
        productSelection: state,
        toggleSelectionMode,
        onSelectButtonClick,
        selectAll,
        deselectAll,

        /**
         * @deprecated
         */
        productSelectionDeprecated: mapProductSelectionToReduxState(state),
        /**
         * @deprecated
         */
        onSelectButtonClickDeprecated: onSelectButtonClickDeprecated
    };
};

/**
 * @deprecated
 */
export const mapProductSelectionToReduxState = (state: ProductSelection): ProductSelectionState => {
    let productSelection: ProductSelectionState = {
        all: false,
        selected: [],
        excluded: [],
        count: 0,
        selectionMode: false
    };

    if (isExcludeProductSelection(state)) {
        productSelection = {
            all: true,
            selected: [],
            excluded: state.excluded,
            count: state.totalCount,
            selectionMode: true
        };
    } else if (isIncludeProductSelection(state)) {
        productSelection = {
            all: false,
            selected: state.included,
            excluded: [],
            count: state.included.length,
            selectionMode: true
        };
    }

    return productSelection;
};

function mapProductIdToLineItemKey(item: LineItemKey | string): LineItemKey {
    return typeof item !== 'string'
        ? item
        : {
              productId: item,
              usageCondition: undefined,
              subscriptionPeriodType: undefined
          };
}
